<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query";
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useMessage } from "naive-ui";
import {
    completeStep,
    getOrderedSteps,
    isOnboardingEnabled,
    isOnboardingInProgress,
    onboardingProgress,
    skipStep,
} from "../../constants/onboardingProgress";
import type { OrderedStep } from "../../constants/onboardingProgress";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints";

const props = defineProps<{
    step: OrderedStep;
}>();

const emit = defineEmits<{
    (e: "completed"): void;
    (e: "skipped"): void;
}>();

const router = useRouter();
const message = useMessage();
const { isDesktop } = usePortalBreakpoints();

const orderedSteps = computed(() => getOrderedSteps());

const currentStep = computed(() =>
    orderedSteps.value.find((s) => s.step_code === onboardingProgress.currentStepCode),
);

const isStepResolved = computed(
    () => props.step.status === "completed" || props.step.status === "skipped",
);

function stepCircleClass(stepCode: string, status: "completed" | "skipped" | "pending" | null) {
    if (status === "completed") {
        return "bg-green-800";
    }

    if (status === "skipped") {
        return "bg-orange-500";
    }

    if (currentStep.value?.step_code === stepCode) {
        return "bg-purple-200";
    }

    return "bg-gray-400";
}

const { mutateAsync: confirmStep, isPending: isConfirming } = useMutation({
    mutationFn: async () => {
        await completeStep(props.step.step_code);
    },
    onSuccess: () => {
        message.success(props.step.success_message);
    },
});

const { mutateAsync: skipCurrentStep, isPending: isSkipping } = useMutation({
    mutationFn: async () => {
        await skipStep(props.step.step_code);
    },
});

const isMutatingStep = computed(() => isConfirming.value || isSkipping.value);

function navigateAfterStepChange() {
    if (onboardingProgress.currentStepCode === "OPEN_ITEMS" && isOnboardingInProgress()) {
        router.push("/open-items");
    } else {
        router.push("/onboarding");
    }
}

async function onConfirm() {
    await confirmStep();
    navigateAfterStepChange();
    emit("completed");
}

async function onSkip() {
    await skipCurrentStep();
    navigateAfterStepChange();
    emit("skipped");
}
</script>

<template>
    <section
        v-if="isOnboardingEnabled()"
        class="mb-6 border-b border-neutral-300 bg-white px-4 py-2.5 flex items-center justify-between"
    >
        <div
            class="flex items-center gap-3 flex-wrap"
            :class="{
                'ml-8': !isDesktop,
            }"
        >
            <button
                class="text-label flex items-center gap-1 active:scale-95"
                @click="router.push('/onboarding')"
            >
                <span class="material-symbols-outlined text-base">arrow_back</span>
                Back
            </button>

            <div class="flex items-center gap-2">
                <span
                    v-for="s in orderedSteps"
                    :key="s.step_code"
                    class="h-2.5 w-2.5 rounded-full"
                    :class="stepCircleClass(s.step_code, s.status)"
                />
            </div>

            <p class="text-sm">
                Step {{ step.step_number }}/{{ orderedSteps.length }} &middot;
                {{ step.title }}
            </p>
        </div>

        <div class="flex items-center gap-3">
            <button class="button" :disabled="isMutatingStep" @click="onConfirm">Done</button>
            <button
                v-if="isStepResolved"
                class="button"
                @click="router.push('/onboarding')"
            >
                Next
            </button>
            <button
                v-else-if="step.is_skippable"
                class="text-label"
                :disabled="isMutatingStep"
                @click="onSkip"
            >
                Skip
            </button>
        </div>
    </section>
</template>
